import { GlobalApplicationState } from "globalApplicationState";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_DRAFT_SMS_FILTERS, DEFAULT_SCHEDULED_SMS_FILTERS, DEFAULT_SENT_SMS_FILTERS, ISMSFilters, ISMSListingPage, SMSStatus } from "./models";
import Tabs from "pages/common/tabs";
import cookie, { ROWS_PER_PAGE_COOKIE_NAMES } from "utils/cookie";
import { getDraftSMSList, getScheduledSMSList, getSentSMSList } from "./reducer";
import CreatedByFilter, { CreatedBySelectValues } from "modules/common/components/authoring/createdByFilter";
import { ContentType } from "modules/common/models";
import SMSList from "./smsList";

interface ISMSListingProps {
    onDownloadSMSActivity: (smsId: string) => void;
}

const SMSListing: React.FC<ISMSListingProps> = ({
    onDownloadSMSActivity
}) => {
    const [selectedTab, setSelectedTab] = useState(SMSStatus.Draft);
    const [draftSMSListFilters, setDraftSMSListFilters] = useState(DEFAULT_DRAFT_SMS_FILTERS);
    const [scheduledSMSListFilters, setScheduledSMSListFilters] = useState(DEFAULT_SCHEDULED_SMS_FILTERS);
    const [sentSMSListFilters, setSentSMSListFilters] = useState(DEFAULT_SENT_SMS_FILTERS);
    const currentFilters = selectedTab === SMSStatus.Draft 
        ? draftSMSListFilters 
        : selectedTab === SMSStatus.Scheduled 
            ? scheduledSMSListFilters 
            : sentSMSListFilters;

    const { draftSMSList, scheduledSMSList, sentSMSList } = useSelector((state: GlobalApplicationState) => state.sms);
    const dispatch = useDispatch();

    const getTabLabel = (label: string, page: ISMSListingPage): string => {
        if (!!page.currentPage)
            return `${label} (${page.totalSMS})`;
        return label;
    }

    const fetchDraftSMSList = useCallback((
        filters: ISMSFilters = DEFAULT_DRAFT_SMS_FILTERS,
        pageNumber: number = 1, 
        pageAmount: number = cookie.getRowsPerPage(ROWS_PER_PAGE_COOKIE_NAMES.SMS, 25)
    ) => 
        dispatch(getDraftSMSList({ pageNumber, pageAmount, filters })), [dispatch]);

    const fetchScheduledSMSList = useCallback((
        filters: ISMSFilters = DEFAULT_SCHEDULED_SMS_FILTERS,
        pageNumber: number = 1, 
        pageAmount: number = cookie.getRowsPerPage(ROWS_PER_PAGE_COOKIE_NAMES.SMS, 25)
    ) => 
        dispatch(getScheduledSMSList({ pageNumber, pageAmount, filters })), [dispatch]);

    const fetchSentSMSList = useCallback((
        filters: ISMSFilters = DEFAULT_SENT_SMS_FILTERS, 
        pageNumber: number = 1, 
        pageAmount: number = cookie.getRowsPerPage(ROWS_PER_PAGE_COOKIE_NAMES.SMS, 25)
    ) => 
        dispatch(getSentSMSList({ pageNumber, pageAmount, filters })), [dispatch]);

    const fetchAllPages = useCallback(() => {
        fetchDraftSMSList(draftSMSListFilters)
        fetchScheduledSMSList(scheduledSMSListFilters);
        fetchSentSMSList(sentSMSListFilters);
    }, [fetchSentSMSList, fetchDraftSMSList, fetchScheduledSMSList, draftSMSListFilters, scheduledSMSListFilters, sentSMSListFilters]);
    
    const onSelectPostCreatedBy = (selection: CreatedBySelectValues) => {
        if (selectedTab === SMSStatus.Draft) {
            const newFilters = { ...draftSMSListFilters, createdByMeOnly: selection === CreatedBySelectValues.VIEW_MY };
            setDraftSMSListFilters(newFilters);
            fetchDraftSMSList(newFilters);
        }
        else if (selectedTab === SMSStatus.Scheduled) {
            const newFilters = { ...scheduledSMSListFilters, createdByMeOnly: selection === CreatedBySelectValues.VIEW_MY };
            setScheduledSMSListFilters(newFilters);
            fetchScheduledSMSList(newFilters);
        }
        else if (selectedTab === SMSStatus.Sent) {
            const newFilters = { ...sentSMSListFilters, createdByMeOnly: selection === CreatedBySelectValues.VIEW_MY };
            setSentSMSListFilters(newFilters);
            fetchSentSMSList(newFilters);
        }
    }

    useEffect(() => {
        fetchAllPages();
    }, [fetchAllPages]);

    return <div className="sms-listing">
        <div className="authoring-tabs-container">
            <Tabs
                tabs={[
                    { label: getTabLabel("Drafts", draftSMSList), dotColor: "grey", value: SMSStatus.Draft, id: "drafts-SMS-tab-button" },
                    { label: getTabLabel("Scheduled", scheduledSMSList), dotColor: "yellow", value: SMSStatus.Scheduled, id: "scheduled-SMS-tab-button" },
                    { label: getTabLabel("Sent", sentSMSList), dotColor: "green", value: SMSStatus.Sent, id: "sent-SMS-tab-button" }
                ]}
                selectedTab={selectedTab}
                onSelectTab={newTab => setSelectedTab(newTab)}
            />
            <CreatedByFilter
                selectedFilter={currentFilters.createdByMeOnly ? CreatedBySelectValues.VIEW_MY : CreatedBySelectValues.VIEW_ALL}
                content={ContentType.SMS}
                onSelect={onSelectPostCreatedBy}
                allCaps
                plural={false}
            />
        </div>
        <SMSList 
            show={selectedTab === SMSStatus.Draft} 
            page={draftSMSList}
            fetchPage={fetchDraftSMSList}
            fetchAllPages={fetchAllPages}
            filters={draftSMSListFilters}
            onUpdateFilters={setDraftSMSListFilters}
            defaultSMSFilterValues={DEFAULT_DRAFT_SMS_FILTERS}
            onDownloadSMSActivity={onDownloadSMSActivity}
        />
        <SMSList 
            show={selectedTab === SMSStatus.Scheduled} 
            page={scheduledSMSList}
            fetchPage={fetchScheduledSMSList}
            fetchAllPages={fetchAllPages}
            filters={scheduledSMSListFilters}
            onUpdateFilters={setScheduledSMSListFilters}
            defaultSMSFilterValues={DEFAULT_SCHEDULED_SMS_FILTERS}
            onDownloadSMSActivity={onDownloadSMSActivity}
        />
        <SMSList 
            show={selectedTab === SMSStatus.Sent}
            page={sentSMSList}
            fetchPage={fetchSentSMSList}
            fetchAllPages={fetchAllPages}
            filters={sentSMSListFilters}
            onUpdateFilters={setSentSMSListFilters}
            defaultSMSFilterValues={DEFAULT_SENT_SMS_FILTERS}
            onDownloadSMSActivity={onDownloadSMSActivity}
        />
    </div>
}

export default SMSListing;